import type { Map as LeafletMap } from 'leaflet';
import { type RefObject, useState } from 'react';

export type MapCurrentLocationState = 'idle' | 'locating' | 'active';
export type MapCurrentLocationCoordinates = {
  latitude: number;
  longitude: number;
} | null;

type Props = {
  mapRef: RefObject<LeafletMap | null>;
  targetZoom?: number;
};

export const useMapCurrentLocation = ({ mapRef, targetZoom = 15 }: Props) => {
  const [locationState, setLocationState] = useState<MapCurrentLocationState>('idle');
  const [currentLocationCoordinates, setCurrentLocationCoordinates] = useState<MapCurrentLocationCoordinates>(null);

  const requestCurrentLocation = (timeout?: number) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationState('active');
        setCurrentLocationCoordinates({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        mapRef.current?.flyTo([position.coords.latitude, position.coords.longitude], targetZoom, { duration: 1 });
      },
      () => {
        setLocationState('idle');
      },
      {
        enableHighAccuracy: false,
        maximumAge: 60000,
        ...(timeout ? { timeout } : {})
      }
    );
  };

  const handleCurrentLocation = () => {
    if (!mapRef.current || !navigator.geolocation) {
      return;
    }

    setLocationState('locating');

    // When permission is still pending, avoid a short timeout so the first click can survive the browser prompt.
    if (!navigator.permissions?.query) {
      requestCurrentLocation();
      return;
    }

    void (async () => {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
        requestCurrentLocation(permissionStatus.state === 'prompt' ? undefined : 5000);
      } catch {
        requestCurrentLocation();
      }
    })();
  };

  return {
    currentLocationCoordinates,
    locationState,
    handleCurrentLocation
  };
};
